import type { Emitter } from "@core/events/emitter";
import type { EntityTransform } from "@models/data/entityData";

/**
 * `IGrabModifierModel` — web port of Unity
 * `Models/Modifiers/IGrabModifierModel.cs` + the option state Unity spreads
 * across `GrabTool` / `DesktopGrabToolViewModel`.
 *
 * Ownership split (mirrors Unity): this model owns grab OPTIONS + the commit
 * policy (undo + persistence under `UpdateEntityFlags.Grab`); the runtime
 * `GrabToolController` owns the Three.js drag math, ghost visual, and the
 * snapback animation; `NetworkGrabUpdateModel` owns the wire contract.
 *
 * Desktop-scope deviations (VR paths intentionally unported): no kinematics /
 * physics joints, no free-mode proximity grab, no haptics. `resetSceneAsync`
 * routes through the shared `resetActiveSceneAsync` (authored-pose restore)
 * instead of Unity's grab-local `ResetAll` — same observable pose outcome on
 * desktop, one shared reset path (`GrabModifierModel.UseHardSceneReset` is
 * false upstream, i.e. legacy pose-preserving reset).
 */
export interface IGrabModifierModel {
  readonly onOptionsChanged: Emitter<void>;
  readonly onGrabStateChanged: Emitter<{ isGrabbing: boolean; snapshotId: string | null }>;

  /** Part (true, Unity default) vs whole-model grab (`GrabTool.UsePartSelection`). */
  readonly usePartSelection: boolean;
  /** Ghost + snapback on release (`GrabTool.IsSnapMode` ⇄ scene `ShouldSnapback`). */
  readonly isSnapMode: boolean;
  /** Snapback overlap threshold (`GrabModifierModel.SnapThreshold`, default 0.1). */
  readonly snapThreshold: number;
  readonly isGrabbing: boolean;
  readonly grabbedSnapshotId: string | null;

  setUsePartSelection(value: boolean): void;
  /** Sets the mode AND persists `SceneInfo.shouldSnapback` (Unity `UseSnapMode`). */
  setSnapMode(value: boolean): void;
  setSnapThreshold(value: number): void;

  /** Controller bookkeeping — drives `onGrabStateChanged` for UI/tooltips. */
  notifyGrabStarted(snapshotId: string): void;
  notifyGrabEnded(): void;
  /**
   * Record a snapshot as grab-touched this session WITHOUT opening a local
   * grab (used for REMOTE grabs — Unity's receive path also routes through
   * `StartGrab`, so remotely moved entities join the modified-object set).
   */
  noteGrabTarget(snapshotId: string): void;
  /**
   * Unity `GetModifiedObjects`: grab-touched snapshots whose current position
   * differs from the hard-reset pose. Drives the presenter's `sync: true`
   * re-broadcast when a participant joins.
   */
  getModifiedSnapshotIds(): ReadonlyArray<string>;

  /**
   * Commit a finished (non-snapback) grab: records ONE undo command whose
   * redo/undo both persist with the Grab flag (Unity `UndoRedo.Record(entity,
   * SetGrabPose, SetGrabPose, newPose, m_initialPose)` — `record` runs the
   * redo immediately, which IS the persistence commit).
   */
  commitGrab(
    sceneId: string,
    snapshotId: string,
    previousTransform: EntityTransform,
    nextTransform: EntityTransform,
  ): void;

  /** Grab panel "Reset Scene" (Unity `GrabTool.ResetAll`, non-undoable). */
  resetSceneAsync(): Promise<void>;
}

export const GRAB_MODIFIER_MODEL = Symbol.for("Campfire.GrabModifierModel");
